import React, { useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Td,
  Text,
} from "@chakra-ui/react";
import { FiDownload, FiTrash2 } from "react-icons/fi";
import { useAutoSaveContext } from "newui/components/ui/AutoSave";
import { SelectOption } from "newui/components/ui/InputWithLabel/SelectWithLabel";
import dayjs from "newui/utils/dayjs";
import { RfxDocumentRow } from "../types/rfx-document.types";
import { InlineEditableCell } from "./InlineEditableCell";

interface DocumentTableRowProps {
  categoryOptions: SelectOption[];
  doc: RfxDocumentRow;
  /** Hide the Revision cell entirely (e.g. during creation flow). */
  hideRevision?: boolean;
  isSelected: boolean;
  onDeleteRequest: (doc: RfxDocumentRow) => void;
  onToggleSelect: (id: number) => void;
  onUpdate: (docId: number, data: { category_id?: number; external_revision?: string }) => Promise<void>;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Renders table cells (Td) for a single document row.
 * Must be placed inside a <Tr> element (e.g. via SortableDocumentRow).
 */
export const DocumentTableRow = ({
  doc,
  isSelected,
  categoryOptions,
  hideRevision = false,
  onToggleSelect,
  onUpdate,
  onDeleteRequest,
}: DocumentTableRowProps) => {
  const autoSave = useAutoSaveContext();
  const [savingField, setSavingField] = useState<"category" | "revision" | null>(null);

  const handleSaveCategory = async (value: string | number) => {
    setSavingField("category");
    autoSave?.setSaving(true);
    try {
      await onUpdate(doc.id, { category_id: Number(value) });
      autoSave?.setLastSavedAt(new Date());
    } catch {
      autoSave?.setError("Failed to save category");
    } finally {
      setSavingField(null);
      autoSave?.setSaving(false);
    }
  };

  const handleSaveRevision = async (value: string | number) => {
    setSavingField("revision");
    autoSave?.setSaving(true);
    try {
      await onUpdate(doc.id, { external_revision: String(value) });
      autoSave?.setLastSavedAt(new Date());
    } catch {
      autoSave?.setError("Failed to save revision");
    } finally {
      setSavingField(null);
      autoSave?.setSaving(false);
    }
  };

  return (
    <>
      <Td>
        <Checkbox
          isChecked={isSelected}
          onChange={() => onToggleSelect(doc.id)}
          data-testid={`document-checkbox-${doc.id}`}
        />
      </Td>
      <Td whiteSpace="normal">
        <Box>
          {/* `wordBreak: break-word` wraps on natural boundaries when possible;
              `overflowWrap: anywhere` is the escape hatch for single-token
              filenames (e.g. `Specification_R02_2026-05_FINAL_v3.pdf`) that
              would otherwise blow the column width out. Drop `noOfLines` so
              long names aren't silently truncated. */}
          <Text
            fontSize="sm"
            fontWeight="medium"
            wordBreak="break-word"
            sx={{ overflowWrap: "anywhere" }}
          >
            {doc.fileName}
          </Text>
        </Box>
      </Td>
      <Td>
        <Text fontSize="sm">{formatFileSize(doc.fileSize)}</Text>
      </Td>
      <Td>
        <InlineEditableCell
          mode="select"
          value={doc.categoryId}
          options={categoryOptions}
          onSave={handleSaveCategory}
          isSaving={savingField === "category"}
          testId={`doc-category-${doc.id}`}
        />
      </Td>
      {/* External revision is user-facing and always editable. Defaults to
          the internal integer revision when the user hasn't set a custom
          value yet ("0" / empty), so the cell never reads as blank. The
          hidden `internal_revision` itself stays under the hood — it's
          bumped server-side on replace and used for our own tracking. */}
      <Td whiteSpace="normal">
        <InlineEditableCell
          mode="text"
          value={
            doc.externalRevision && doc.externalRevision !== "0"
              ? doc.externalRevision
              : String(doc.internalRevision ?? 0)
          }
          onSave={handleSaveRevision}
          isSaving={savingField === "revision"}
          testId={`doc-revision-${doc.id}`}
        />
      </Td>
      <Td>
        <Text fontSize="xs" color="gray.600">
          {dayjs(doc.createdAt).format("DD MMM YYYY")}
        </Text>
      </Td>
      <Td>
        <Text fontSize="xs" color="gray.600">
          {doc.uploadedBy}
        </Text>
      </Td>
      <Td>
        <Box display="flex" gap={1}>
          <IconButton
            aria-label="Download document"
            icon={<FiDownload />}
            size="sm"
            variant="ghost"
            as="a"
            href={doc.documentPath}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`doc-download-${doc.id}`}
          />
          <IconButton
            aria-label="Delete document"
            icon={<FiTrash2 />}
            size="sm"
            variant="ghost"
            colorScheme="red"
            onClick={() => onDeleteRequest(doc)}
            data-testid={`doc-delete-${doc.id}`}
          />
        </Box>
      </Td>
    </>
  );
};
